if (!customElements.get('mcb-planner-gallery')) {
  customElements.define('mcb-planner-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      queueMicrotask(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected) return;
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbnails = Array.from(this.querySelectorAll('[data-gallery-select]'));
      this.dialog = this.querySelector('[data-gallery-dialog]');
      this.dialogImage = this.querySelector('[data-gallery-dialog-image]');
      this.imageStatus = this.querySelector('[data-gallery-image-status]');
      this.dialogScroll = this.querySelector('[data-gallery-scroll]');
      this.current = 0;
      this.ignoreClickUntil = 0;
      const options = { signal: this.controller.signal };
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-gallery-autoplay')) {
          const paused = this.userPaused || this.focusPaused;
          this.userPaused = !paused;
          this.focusPaused = false;
          this.refreshAutoplay();
        }
        if (button.hasAttribute('data-gallery-select')) this.select(Number(button.dataset.gallerySelect), true);
        if (button.hasAttribute('data-gallery-prev')) this.select(this.current - 1, true);
        if (button.hasAttribute('data-gallery-next')) this.select(this.current + 1, true);
        if (button.hasAttribute('data-gallery-open') && Date.now() >= this.ignoreClickUntil) this.open(button);
        if (button.hasAttribute('data-gallery-close')) this.dialog.close();
        if (button.hasAttribute('data-gallery-zoom')) {
          this.endPan();
          const zoomed = this.dialog.classList.toggle('is-zoomed');
          button.setAttribute('aria-pressed', String(zoomed));
          button.textContent = zoomed ? this.dataset.zoomOutLabel : this.dataset.zoomInLabel;
          this.updatePanAvailability();
        }
      }, options);
      this.addEventListener('keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        if (this.dialog.open && this.dialog.classList.contains('is-zoomed')) return;
        event.preventDefault();
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.current + (event.key === 'ArrowRight' ? 1 : -1);
        this.select(index, true);
        if (event.target.closest('[data-gallery-select]')) this.thumbnails[this.current]?.focus({ preventScroll: true });
        if (event.target.closest('[data-gallery-open]')) this.slides[this.current]?.querySelector('[data-gallery-open]')?.focus({ preventScroll: true });
      }, options);
      this.dialogImage.addEventListener('load', () => this.setImageState('ready'), options);
      this.dialogImage.addEventListener('error', () => this.setImageState('error'), options);
      this.initializePan(options);
      const stage = this.querySelector('[data-gallery-stage]');
      stage.addEventListener('pointerdown', event => {
        if (!this.swipeMode?.matches && !this.closest('mcb-planner-editions, [data-cta-carusel]') && event.isPrimary && event.pointerType !== 'mouse') this.swipe = { x: event.clientX, y: event.clientY };
      }, options);
      stage.addEventListener('pointerup', event => {
        if (!this.swipe) return;
        const dx = event.clientX - this.swipe.x;
        const dy = event.clientY - this.swipe.y;
        this.swipe = null;
        if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
        this.ignoreClickUntil = Date.now() + 400;
        this.select(this.current + (dx < 0 ? 1 : -1), true);
      }, options);
      stage.addEventListener('pointercancel', () => { this.swipe = null; }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => {
        this.endPan();
        this.dialog.classList.remove('is-pannable');
        document.body.style.overflow = this.previousOverflow || '';
        const opener = this.slides[this.current]?.querySelector('[data-gallery-open]') || this.opener;
        opener?.focus();
      }, options);
      if (this.hasAttribute('data-autoplay')) this.initializeAutoplay(options);
      if (this.hasAttribute('data-swipe')) this.initializeSwipe(stage, options);
    }

    // Sub 750px toate foile stau pe un rând cu scroll-snap (vezi CSS), deci swipe-ul e al browserului.
    initializeSwipe(stage, options) {
      this.stage = stage;
      this.swipeMode = matchMedia('(max-width: 749px)');
      let frame = 0;
      stage.addEventListener('scroll', () => {
        if (!this.swipeMode.matches || frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          const index = Math.round(stage.scrollLeft / stage.clientWidth);
          if (index !== this.current) this.select(index, true, false);
        });
      }, options);
      this.swipeMode.addEventListener('change', () => this.select(this.current), options);
    }

    initializePan(options) {
      this.dialogScroll.addEventListener('pointerdown', event => {
        // Touch uses the browser's native two-axis scrolling and pinch zoom.
        if (event.pointerType === 'touch' || !event.isPrimary || event.button !== 0) return;
        if (!this.dialog.open || !this.dialog.classList.contains('is-pannable')) return;
        if (event.target !== this.dialogImage && event.target !== this.dialogScroll) return;
        const bounds = this.dialogScroll.getBoundingClientRect();
        // Leave the scrollbars available for normal dragging.
        if (event.clientX >= bounds.left + this.dialogScroll.clientWidth || event.clientY >= bounds.top + this.dialogScroll.clientHeight) return;
        event.preventDefault();
        this.pan = { id: event.pointerId, x: event.clientX, y: event.clientY };
        this.dialogScroll.setPointerCapture(event.pointerId);
        this.dialog.classList.add('is-dragging');
      }, options);
      this.dialogScroll.addEventListener('pointermove', event => {
        if (!this.pan || event.pointerId !== this.pan.id) return;
        if (!(event.buttons & 1)) { this.endPan(); return; }
        event.preventDefault();
        this.dialogScroll.scrollTo({
          left: this.dialogScroll.scrollLeft + this.pan.x - event.clientX,
          top: this.dialogScroll.scrollTop + this.pan.y - event.clientY,
          behavior: 'instant'
        });
        this.pan.x = event.clientX;
        this.pan.y = event.clientY;
      }, options);
      const finish = event => {
        if (event.pointerId === this.pan?.id) this.endPan();
      };
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(type => this.dialogScroll.addEventListener(type, finish, options));
      window.addEventListener('blur', () => this.endPan(), options);
      this.panObserver = new ResizeObserver(() => this.updatePanAvailability());
      this.panObserver.observe(this.dialogScroll);
      this.panObserver.observe(this.dialogImage);
    }

    updatePanAvailability() {
      const available = this.dialog.open && !this.dialogImage.hidden &&
        (this.dialogScroll.scrollWidth > this.dialogScroll.clientWidth || this.dialogScroll.scrollHeight > this.dialogScroll.clientHeight);
      this.dialog.classList.toggle('is-pannable', available);
      if (!available) this.endPan();
    }

    endPan() {
      const pan = this.pan;
      this.pan = null;
      this.dialog.classList.remove('is-dragging');
      if (pan && this.dialogScroll.hasPointerCapture(pan.id)) this.dialogScroll.releasePointerCapture(pan.id);
    }

    initializeAutoplay(options) {
      this.motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.motionPreference.matches;
      this.focusPaused = false;
      this.hoverPaused = false;
      this.inView = false;
      this.addEventListener('mouseenter', () => { this.hoverPaused = true; this.refreshAutoplay(); }, options);
      this.addEventListener('mouseleave', () => { this.hoverPaused = false; this.refreshAutoplay(); }, options);
      this.addEventListener('focusin', event => {
        if (event.target.closest('[data-gallery-autoplay]')) return;
        this.focusPaused = true;
        this.refreshAutoplay();
      }, options);
      document.addEventListener('visibilitychange', () => this.refreshAutoplay(), options);
      this.motionPreference.addEventListener('change', () => {
        if (this.motionPreference.matches) this.userPaused = true;
        this.refreshAutoplay();
      }, options);
      this.viewObserver = new IntersectionObserver(entries => {
        this.inView = entries[0].intersectionRatio >= .35;
        this.refreshAutoplay();
      }, { threshold: .35 });
      this.viewObserver.observe(this);
      this.refreshAutoplay();
    }

    refreshAutoplay() {
      if (!this.hasAttribute('data-autoplay')) return;
      clearTimeout(this.autoplayTimer);
      const paused = this.userPaused || this.focusPaused;
      const button = this.querySelector('[data-gallery-autoplay]');
      button.textContent = paused ? this.dataset.playLabel : this.dataset.pauseLabel;
      button.setAttribute('aria-label', button.textContent);
      const running = !paused && !this.hoverPaused && this.inView && !document.hidden && !this.dialog.open;
      this.querySelector('[data-gallery-count]').setAttribute('aria-live', running ? 'off' : 'polite');
      if (running) {
        this.autoplayTimer = setTimeout(() => {
          this.select(this.current + 1);
          this.refreshAutoplay();
        }, Number(this.dataset.autoplay) || 8000);
      }
    }

    select(index, revealThumbnail = false, moveStage = true) {
      if (!this.slides.length) return;
      if (revealThumbnail && this.closest('mcb-planner-editions, [data-targuri-cta]')) this.setAttribute('data-gallery-browsing', '');
      if (revealThumbnail && this.hasAttribute('data-autoplay')) {
        this.userPaused = true;
        this.refreshAutoplay();
      }
      this.current = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => { slide.hidden = i !== this.current; });
      this.thumbnails.forEach((thumbnail, i) => {
        if (i === this.current) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
      });
      const counter = this.querySelector('[data-gallery-count]');
      if (counter) counter.textContent = this.countLabel();
      if (this.swipeMode) {
        this.querySelector('[data-gallery-contor]').textContent = `${this.current + 1} / ${this.slides.length}`;
        this.querySelector('[data-gallery-eticheta]').textContent = this.thumbnails[this.current].querySelector('span').textContent;
        if (moveStage && this.swipeMode.matches) this.stage.scrollTo({ left: this.stage.clientWidth * this.current, behavior: 'instant' });
      }
      if (revealThumbnail && this.thumbnails.length) {
        const selected = this.thumbnails[this.current];
        const track = selected.parentElement;
        const selectedBounds = selected.getBoundingClientRect();
        const trackBounds = track.getBoundingClientRect();
        if (selectedBounds.left < trackBounds.left || selectedBounds.right > trackBounds.right) {
          track.scrollTo({ left: track.scrollLeft + selectedBounds.left - trackBounds.left, behavior: 'instant' });
        }
      }
      if (this.dialog.open) this.updateDialog();
    }

    countLabel() {
      return this.dataset.countTemplate.replace('__CURRENT__', String(this.current + 1)).replace('__TOTAL__', String(this.slides.length));
    }

    setImageState(state) {
      this.querySelector('[data-gallery-scroll]').setAttribute('aria-busy', String(state === 'loading'));
      this.dialogImage.hidden = state !== 'ready';
      this.imageStatus.hidden = state === 'ready';
      this.imageStatus.textContent = state === 'error' ? this.dataset.errorLabel : this.dataset.loadingLabel;
      this.updatePanAvailability();
    }

    updateDialog() {
      this.endPan();
      const slide = this.slides[this.current];
      this.setImageState('loading');
      this.dialogImage.src = slide.dataset.full;
      this.dialogImage.alt = slide.dataset.title;
      const sourceImage = slide.querySelector('img');
      this.dialogImage.width = Number(sourceImage.getAttribute('width'));
      this.dialogImage.height = Number(sourceImage.getAttribute('height'));
      this.querySelector('[data-gallery-dialog-title]').textContent = slide.dataset.title;
      this.dialog.setAttribute('aria-label', slide.dataset.title);
      const counter = this.querySelector('[data-gallery-dialog-count]');
      if (counter) counter.textContent = this.countLabel();
      this.dialog.classList.remove('is-zoomed');
      const zoom = this.querySelector('[data-gallery-zoom]');
      zoom.setAttribute('aria-pressed', 'false');
      zoom.textContent = this.dataset.zoomInLabel;
      this.querySelector('[data-gallery-scroll]').scrollTo(0, 0);
      if (this.dialogImage.complete) this.setImageState(this.dialogImage.naturalWidth ? 'ready' : 'error');
    }

    open(button) {
      if (this.hasAttribute('data-autoplay')) {
        this.userPaused = true;
        this.refreshAutoplay();
      }
      this.opener = button;
      this.previousOverflow = document.body.style.overflow;
      this.updateDialog();
      this.dialog.showModal();
      this.updatePanAvailability();
      document.body.style.overflow = 'hidden';
      this.querySelector('[data-gallery-close]').focus({ preventScroll: true });
    }

    disconnectedCallback() {
      clearTimeout(this.autoplayTimer);
      this.viewObserver?.disconnect();
      this.panObserver?.disconnect();
      if (this.dialog) this.endPan();
      if (this.dialog?.open) {
        this.dialog.close();
        document.body.style.overflow = this.previousOverflow || '';
      }
      this.controller?.abort();
      this.controller = null;
    }
  });
}
